import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useRealtimeCtx } from "@/realtime/RealtimeProvider";
import NotificationBell from "@/realtime/NotificationBell";
import { setTheme as setPlatformTheme } from "@/lib/theme";
import { useT } from "../i18n";

/* ──────────────────────────────────────────────────────────────────────────
   LhcHeader — the original prototype's dark top bar, ported to React.
   Used on the home / clients dashboard (the "non-configurator" header variant):
   brand wordmark · centered global search · notifications · theme toggle · user.
   Styling comes entirely from the ported prototype stylesheet (lhc-prototype.css).
   ────────────────────────────────────────────────────────────────────────── */
export default function LhcHeader({ search = "", onSearch, showSearch = true, center = null, rightExtras = null }) {
  const t = useT();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const rt = useRealtimeCtx(); // real notification bundle (null if not inside a RealtimeProvider)
  const [userOpen, setUserOpen] = useState(false);
  const wrapRef = useRef(null);

  const b = user?.branding_context || {};
  const name = user?.name || "User";
  const initials = name.split(/\s+/).map((w) => w[0] || "").slice(0, 2).join("").toUpperCase() || "U";

  // Close the user menu on outside click / Escape (the bell manages its own dropdown).
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setUserOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setUserOpen(false); };
    document.addEventListener("click", onDoc, true);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("click", onDoc, true); document.removeEventListener("keydown", onKey); };
  }, []);

  const toggleTheme = () => {
    const cur = document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
    // ONE theme engine (V5): writes the canonical key, mirrors the legacy
    // catalog key, stamps html+body and broadcasts to every mounted app.
    setPlatformTheme(cur === "dark" ? "light" : "dark");
  };

  const signOut = () => { setUserOpen(false); logout().finally(() => nav("/login")); };

  return (
    <>
      <header className="header header-dark">
        {/* LEFT: brand */}
        <div className="header-left">
          <button className="brand-btn" type="button" title="LHC Platform" onClick={() => nav("/store")}>
            <span className="logo">LHC</span>
            <span className="logo-studio">Platform</span>
          </button>
        </div>

        {/* CENTER: global search (home) or a custom slot (e.g. configurator context) */}
        {showSearch ? (
          <div className="header-search-wrap">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></svg>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearch?.(e.target.value)}
              placeholder={t("nav.search")}
              data-testid="header-search"
            />
          </div>
        ) : center ? (
          <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", display: "flex", alignItems: "center" }}>
            {center}
          </div>
        ) : null}

        {/* RIGHT */}
        <div className="header-right">
          {rightExtras}
          {/* Real notification bell — live WebSocket unread badge + dropdown feed
              (replaces the old hardcoded bell). Falls back to a static bell if the
              header is ever rendered outside a RealtimeProvider. */}
          {rt ? (
            <span className="header-bell header-bell-live">
              <NotificationBell
                notifications={rt.notifications} unread={rt.unread} connected={rt.connected}
                onMarkRead={rt.markRead} onMarkAllRead={rt.markAllRead}
                onOpen={(n) => { if (n && n.entity_id) nav("/store/clients"); }} />
            </span>
          ) : (
            <button className="header-bell" type="button" title={t("nav.notifications")} disabled>
              <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M18 16v-5a6 6 0 0 0-12 0v5l-2 2v1h16v-1l-2-2z" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
            </button>
          )}

          <button className="header-theme-btn" type="button" title="Toggle light / dark theme" onClick={toggleTheme}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
          </button>

          <div className="header-user-wrap" ref={wrapRef}>
            <button className="header-user" type="button" title="Account" onClick={() => setUserOpen((v) => !v)} data-testid="header-user">
              <span className="header-user-avatar">{initials}</span>
              <span className="header-user-name">{name}</span>
              <svg className="header-user-caret" viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="m4 6 4 4 4-4" /></svg>
            </button>
            <div className="header-user-menu" hidden={!userOpen}>
              <div className="user-menu-head">
                <div className="user-menu-avatar">{initials}</div>
                <div className="user-menu-info">
                  <div className="user-menu-name">{name}</div>
                  <div className="user-menu-email">{user?.email}</div>
                </div>
              </div>
              <div className="user-menu-sep" />
              <button className="user-menu-item" type="button" onClick={() => { setUserOpen(false); nav("/store/account"); }} data-testid="header-account">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                <span>Account</span>
              </button>
              <button className="user-menu-item" type="button" onClick={() => { setUserOpen(false); nav("/store/account"); }} data-testid="header-settings">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                <span>{t("nav.settings")}</span>
              </button>
              <div className="user-menu-sep" />
              <button className="user-menu-item danger" type="button" onClick={signOut} data-testid="header-signout">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                <span>{t("nav.signOut")}</span>
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
